import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { observable, computed, action } from 'mobx';
import { observer } from 'mobx-react';
import { Button, Popup } from 'semantic-ui-react';
import styled, { css } from 'styled-components';
import { FilterBase, TargetBase, TargetDateRangePicker } from '@code-yellow/spider';
import { DateTime, Interval } from 'luxon';


const LUXON_SERVER_DATE_FORMAT = 'yyyy-LL-dd';


const ValueButtonContainer = styled.div`
    display: flex;
    gap: 1em;
    margin-bottom: 1em;
    width: calc(350px - 6rem - 2px);
    > button {
        margin: 0 !important;
        flex: 1 1 0;
        white-space: nowrap;
        padding-left: 0 !important;
        padding-right: 0 !important;
    }
    &:first-child {
        margin-top: 0.167rem;
    }
    &:last-child {
        margin-bottom: 0.167rem;
    }

    ${({ labeled }) => labeled && css`
        position: relative;
        padding-top: 1rem;
        border-top: 1px solid rgba(34, 36, 38, 0.15);

        > label {
            position: absolute;
            left: 50%;
            top: -1px;
            transform: translate(-50%, -50%);
            background-color: #FFF;
            padding: 0 0.5rem;
        }
    `}
`;

const StyledTargetDateRangePicker = styled(TargetDateRangePicker)`
    margin-top: 0.5em !important;
`;

@observer
export class FilterTargetSmartDatePicker extends FilterBase {
    static propTypes = {
        ...FilterBase.propTypes,
        store: PropTypes.object.isRequired,
        name: PropTypes.string.isRequired,
        afterChange: PropTypes.func.isRequired,
    };

    getValue() {
        const { store, name, value } = this.props;
        if (value !== undefined) {
            return value;
        }

        if (store.params[name]) {
            return store.params[name];
        } else {
            return null;
        }
    }

    setInput(value) {
        const { store, name, afterChange } = this.props;

        if (value === null || value === undefined) {
            delete store.params[name];
        } else {
            // Convert Luxon Interval to "start,end" format for storage
            if (value instanceof Interval) {
                const start = value.start.toFormat(LUXON_SERVER_DATE_FORMAT);
                const end = value.end.toFormat(LUXON_SERVER_DATE_FORMAT);
                store.params[name] = `${start},${end}`;
            } else {
                store.params[name] = value;
            }
        }

        if (afterChange) {
            afterChange();
        }
    };

    handleChange(value) {
        this.setInput(value);
    }

    renderContent() {
        const { name, onChange } = this.props;

        const value = this.getValue();

        return (
            <TargetSmartDateRange
                name={name}
                value={value}
                onChange={onChange || this.handleChange.bind(this)}
            />
        );
    }
}



export default class TargetSmartDateRange extends TargetBase {
    constructor(...args) {
        super(...args);
        this.dateRangeRef = this.dateRangeRef.bind(this);
        this.onOpenPopup = this.onOpenPopup.bind(this);
        this.onClosePopup = this.onClosePopup.bind(this);
    }

    dateRangeNode = null;

    dateRangeRef(node) {
        this.dateRangeNode = node;
    }

    @observable openPopup = false;

    @action onOpenPopup() {
        console.log('open');
        this.openPopup = true;
    }

    @action onClosePopup() {
        console.log('close');
        this.openPopup = false;
    }

    @observable selectedCustom = false;

    @computed get selectedValue() {
        // Check for custom date range - either as Interval object or as "start,end" string
        const isCustomRange = this.selectedCustom ||
                              this.value instanceof Interval ||
                              (typeof this.value === 'string' && this.value.includes(','));
        if (isCustomRange) {
            return 'custom';
        } else {
            return this.value ?? 'all_time';
        }
    }

    @computed get intervalValue() {
        // Use fromStore to parse string format back to Interval
        const parsed = this.fromStore(this.value);
        if (parsed instanceof Interval) {
            return parsed;
        }
        return null;
    }

    @action onChange(value) {
        this.onClosePopup();
        if (value === 'custom') {
            this.selectedCustom = true;
            setTimeout(() => {
                const node = ReactDOM.findDOMNode(this.dateRangeNode);
                if (node) {
                    node.querySelector('.start-date > input').focus();
                }
            }, 0);
        } else {
            this.selectedCustom = false;
            super.onChange(value === 'all_time' ? null : value);
        }
    }

    fromStore(value) {
        if (value === undefined || value === null) {
            return null;
        }
        if (typeof value !== 'string' || !value.includes(',')) {
            return value;
        }

        let [start, end] = value.split(',');
        return Interval.fromDateTimes(
            DateTime.fromFormat(start, LUXON_SERVER_DATE_FORMAT),
            DateTime.fromFormat(end, LUXON_SERVER_DATE_FORMAT),
        );
    }

    toStore(value) {
        if (value === null) {
            return undefined;
        }
        if (!(value instanceof Interval)) {
            return value;
        }

        const start = value.start.toFormat(LUXON_SERVER_DATE_FORMAT);
        const end = value.end.toFormat(LUXON_SERVER_DATE_FORMAT);
        return `${start},${end}`;
    }

    renderContent() {
        return (
            <>
                <Popup
                    on="click"
                    open={this.openPopup}
                    onOpen={this.onOpenPopup}
                    onClose={this.onClosePopup}
                    trigger={<Button fluid content={t(`form.smartDateRange.value.${this.selectedValue ?? 'none'}`)} />}
                    content={
                        <>
                            <ValueButtonContainer>
                                <ValueButton target={this} value="today" />
                                <ValueButton target={this} value="yesterday" />
                            </ValueButtonContainer>
                            <ValueButtonContainer>
                                <ValueButton target={this} value="past_7_days" />
                                <ValueButton target={this} value="past_30_days" />
                            </ValueButtonContainer>
                            <ValueButtonContainer labeled>
                                <label>{t('form.smartDateRange.period.last')}</label>
                                <ValueButton target={this} value="last_month" />
                                <ValueButton target={this} value="last_quarter" />
                                <ValueButton target={this} value="last_year" />
                            </ValueButtonContainer>
                            <ValueButtonContainer labeled>
                                <label>{t('form.smartDateRange.period.this')}</label>
                                <ValueButton target={this} value="this_month" />
                                <ValueButton target={this} value="this_quarter" />
                                <ValueButton target={this} value="this_year" />
                            </ValueButtonContainer>
                            <ValueButtonContainer>
                                <ValueButton target={this} value="all_time" />
                            </ValueButtonContainer>
                            <ValueButtonContainer>
                                <ValueButton target={this} value="custom" />
                            </ValueButtonContainer>
                        </>
                    }
                />
                {this.selectedValue === 'custom' && (
                    <StyledTargetDateRangePicker noLabel includeWeeks
                        value={this.intervalValue}
                        onChange={this.onChange}
                        ref={this.dateRangeRef}
                    />
                )}
            </>
        );
    }
}

@observer
class ValueButton extends Component {
    static propTypes = {
        target: PropTypes.object.isRequired,
        value: PropTypes.string.isRequired,
    };

    constructor(...args) {
        super(...args);
        this.onClick = this.onClick.bind(this);
    }

    @computed get active() {
        const { target, value } = this.props;
        return target.selectedValue === value;
    }

    @action onClick() {
        const { target, value } = this.props;
        target.onChange(value);
    }

    @computed get content() {
        const { value } = this.props;
        if (value.startsWith('last_') || value.startsWith('this_')) {
            return t(`form.smartDateRange.period.value.${value.slice(5)}`);
        } else {
            return t(`form.smartDateRange.value.${value}`);
        }
    }

    render() {
        return (
            <Button basic fluid
                content={this.content}
                active={this.active}
                onClick={this.onClick}
            />
        );
    }
}
